import { isAxiosError, type AxiosRequestConfig } from 'axios';

import { getConfiguracaoAtual, setConfiguracaoAtual } from '@/lib/secretario/configuracaoAtual';

import { api } from './client';

// Portal de configuração do Secretário de IA (PLANO §9). Módulo separado de
// propósito — não cresce o client.ts monolítico. Espelha os schemas Pydantic de
// backend/app/schemas/secretario_config.py.

export interface EnderecoAdvogado {
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

export interface Canal {
  id: string;
  provider: string;
  numero_e164: string;
  credencial_ref?: string | null;
  status: string;
}

export interface DocumentoRequerido {
  id: string;
  nome: string;
  descricao?: string | null;
  tipo?: string | null;
  condicao?: string | null;
  obrigatorio: boolean;
  ordem: number;
}

export interface PerguntaQualificacao {
  id: string;
  texto: string;
  tipo?: string | null;
  condicao?: string | null;
  obrigatoria: boolean;
  ordem: number;
}

export interface Area {
  id: string;
  nome: string;
  ativo: boolean;
  /** De qual área do catálogo veio (nulo = criada à mão). */
  catalogo_chave?: string | null;
  advogado_responsavel_id?: string | null;
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
  documentos_requeridos: DocumentoRequerido[];
  perguntas: PerguntaQualificacao[];
}

export interface ItemPendencia {
  chave: string;
  descricao: string;
}

/** O que a área faz: ``pronta`` = fecha contrato e procuração; senão faz o
 * pré-atendimento, e as pendências dizem o que falta. Nenhuma impede a ativação. */
export interface ProntidaoArea {
  area_id: string;
  nome: string;
  pronta: boolean;
  pendencias: ItemPendencia[];
}

/** O advogado é oferecido ao cliente? Se não, o que falta (a regra da conversa). */
export interface ProntidaoParticipante {
  usuario_id: string;
  pronto: boolean;
  pendencias: ItemPendencia[];
}

export interface Prontidao {
  /** Pode ativar: ``pendencias`` é tudo o que impede. */
  pronta: boolean;
  pendencias: ItemPendencia[];
  por_area: ProntidaoArea[];
  por_participante?: ProntidaoParticipante[];
}

/** PESSOAL ("só meu") | ESCRITORIO (fala pelos advogados escolhidos). */
export type EscopoConfiguracao = 'PESSOAL' | 'ESCRITORIO';
/** O papel de quem vê: DONO (o pessoal), GESTOR (do escritório), PARTICIPANTE. */
export type PapelNoSecretario = 'DONO' | 'GESTOR' | 'PARTICIPANTE';

/** Um advogado por quem o Secretário do escritório fala, com o perfil dele. */
export interface Participante {
  usuario_id: string;
  /** Nome do cadastro. */
  nome: string;
  tratamento?: TratamentoAdvogado | null;
  /** Como o Secretário o chama; nulo = nome do cadastro. */
  nome_tratamento?: string | null;
  ativo: boolean;
  ordem: number;
  /** Honorários gerais DELE; sem eles, valem os do escritório. */
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
  advogado_endereco?: EnderecoAdvogado | null;
  /** O Secretário o oferece ao cliente. */
  pronto: boolean;
  pendencias: ItemPendencia[];
}

/** Uma linha do seletor de Secretários. */
export interface ConfiguracaoAcessivel {
  id: string;
  escopo: EscopoConfiguracao;
  escritorio_id?: string | null;
  escritorio_nome?: string | null;
  nome_exibicao: string;
  ativo: boolean;
  papel: PapelNoSecretario;
}

/** Tratamento que o advogado escolheu para si. Ausente = pelo nome. */
export type TratamentoAdvogado = 'DR' | 'DRA';

export interface Configuracao {
  id: string;
  usuario_id: string;
  escritorio_id?: string | null;
  escopo?: EscopoConfiguracao;
  /** O papel de quem pede (a API confere de novo a cada ação). */
  papel?: PapelNoSecretario;
  participantes?: Participante[];
  nome_exibicao: string;
  ativo: boolean;
  saudacao?: string | null;
  persona?: string | null;
  consentimento_texto?: string | null;
  advogado_endereco?: EnderecoAdvogado | null;
  advogado_tratamento?: TratamentoAdvogado | null;
  /** Como o Secretário chama o advogado na conversa; nulo = nome do cadastro. */
  advogado_nome_tratamento?: string | null;
  advogado_titular_id?: string | null;
  exigir_revisao_advogado: boolean;
  /** Prazo (horas) que o secretário promete ao cliente ao escalar o atendimento. */
  prazo_retorno_horas: number;
  /** Honorários gerais; a área com os próprios os substitui por inteiro. */
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
  canal?: Canal | null;
  areas: Area[];
  documentos_base: DocumentoRequerido[];
  perguntas_base: PerguntaQualificacao[];
  prontidao: Prontidao;
  created_at: string;
  updated_at: string;
}

// -- payloads -----------------------------------------------------------------
export interface ConfiguracaoInput {
  nome_exibicao: string;
  saudacao?: string | null;
  persona?: string | null;
  consentimento_texto?: string | null;
  advogado_endereco?: EnderecoAdvogado | null;
  advogado_tratamento?: TratamentoAdvogado | null;
  /** Como o Secretário chama o advogado na conversa; nulo = nome do cadastro. */
  advogado_nome_tratamento?: string | null;
  advogado_titular_id?: string | null;
  exigir_revisao_advogado?: boolean;
  prazo_retorno_horas?: number;
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
}

export interface AreaInput {
  nome: string;
  ativo?: boolean;
  advogado_responsavel_id?: string | null;
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
}

export interface DocumentoRequeridoInput {
  nome: string;
  descricao?: string | null;
  tipo?: string | null;
  condicao?: string | null;
  obrigatorio?: boolean;
  ordem?: number;
}

export interface PerguntaInput {
  texto: string;
  tipo?: string | null;
  condicao?: string | null;
  obrigatoria?: boolean;
  ordem?: number;
}

// Modelo de documento (peticao_modelo) para os seletores de template por área.
export interface ModeloResumo {
  id: string;
  titulo: string;
  finalidade: string;
  /** Do próprio advogado (editável direto); senão é do sistema e editar = copiar. */
  proprio?: boolean;
}

// Tom de voz = conjunto FECHADO (presets curados; o texto que vai ao prompt é do
// backend). Não é texto livre — evita prompt injection via configuração do tenant.
export const PERSONAS: { value: string; label: string }[] = [
  { value: 'FORMAL', label: 'Formal' },
  { value: 'CORDIAL', label: 'Cordial' },
  { value: 'OBJETIVO', label: 'Objetivo' },
  { value: 'ACOLHEDOR', label: 'Acolhedor' },
];

const BASE = '/secretario/configuracao';

/**
 * As chamadas de UMA configuração levam a escolhida na tela (o pessoal ou a de um
 * escritório — docs/PLANO_SECRETARIO_ESCRITORIO.md §6). Sem escolha, o backend usa
 * a primeira do seletor. As de um atendimento pelo id NÃO levam: o backend acha o
 * atendimento entre as configurações do usuário, e o link do aviso funciona com
 * qualquer escolha.
 */
function comConfig(cfg?: AxiosRequestConfig): AxiosRequestConfig | undefined {
  const id = getConfiguracaoAtual();
  if (!id) return cfg;
  return { ...cfg, params: { configuracao_id: id, ...(cfg?.params ?? {}) } };
}

const sec = {
  get: <T>(url: string, cfg?: AxiosRequestConfig) => api.get<T>(url, comConfig(cfg)),
  delete: (url: string, cfg?: AxiosRequestConfig) => api.delete(url, comConfig(cfg)),
  post: <T>(url: string, body?: unknown, cfg?: AxiosRequestConfig) => api.post<T>(url, body, comConfig(cfg)),
  put: <T>(url: string, body?: unknown, cfg?: AxiosRequestConfig) => api.put<T>(url, body, comConfig(cfg)),
};

// -- Cota do add-on (docs/PLANO_SECRETARIO_ADDON.md) ---------------------------
export type FaixaCota = 'NORMAL' | 'ATENCAO' | 'ESGOTADA' | 'SEM_DIREITO';

export interface CotaSecretario {
  habilitado: boolean;
  /** ADDON | PLANO (trial) | null */
  origem: string | null;
  mes: string;
  atendimentos_usados: number;
  /** null = sem limite */
  atendimentos_max: number | null;
  faixa: FaixaCota;
  /** sem_direito | cota_esgotada | teto_interno | null */
  motivo_atendimento_minimo: string | null;
}

export async function fetchCotaSecretario(): Promise<CotaSecretario> {
  const { data } = await sec.get<CotaSecretario>(`${BASE}/cota`);
  return data;
}

// -- Configuração -------------------------------------------------------------
/** A configuração do advogado, ou ``null`` se ele ainda não criou nenhuma. */
export async function fetchConfiguracao(): Promise<Configuracao | null> {
  try {
    const { data } = await sec.get<Configuracao | null>(BASE);
    return data ?? null;
  } catch (error) {
    // A escolha guardada não vale mais (saiu do escritório, foi tirado): cai na
    // primeira do usuário — em qualquer tela, não só na do Secretário.
    if (isAxiosError(error) && error.response?.status === 404 && getConfiguracaoAtual()) {
      setConfiguracaoAtual(null);
      const { data } = await api.get<Configuracao | null>(BASE);
      return data ?? null;
    }
    throw error;
  }
}

/** Sem ``escritorio_id``, o Secretário pessoal ("só meu"); com ele, o do escritório
 * (só o Proprietário). */
/** Uma configuração pelo id (o dossiê sabe de qual Secretário é o atendimento). */
export async function fetchConfiguracaoPorId(configuracaoId: string): Promise<Configuracao> {
  const { data } = await api.get<Configuracao>(BASE, { params: { configuracao_id: configuracaoId } });
  return data;
}

export async function criarConfiguracao(
  payload: ConfiguracaoInput & { escritorio_id?: string | null },
): Promise<Configuracao> {
  const { data } = await api.post<Configuracao>(BASE, payload);
  return data;
}

export async function atualizarConfiguracao(payload: Partial<ConfiguracaoInput>): Promise<Configuracao> {
  const { data } = await sec.put<Configuracao>(BASE, payload);
  return data;
}

export async function ativarSecretario(): Promise<Configuracao> {
  const { data } = await sec.post<Configuracao>(`${BASE}/ativar`);
  return data;
}

export async function desativarSecretario(): Promise<Configuracao> {
  const { data } = await sec.post<Configuracao>(`${BASE}/desativar`);
  return data;
}

// -- Canal --------------------------------------------------------------------
export interface ConexaoWhatsapp {
  conectado: boolean;
  numero?: string | null;
  /** PNG do QR code em data URL, enquanto o celular não lê. */
  qr_base64?: string | null;
}

/**
 * Gera o QR code para conectar o WhatsApp do advogado. Chamar de novo devolve um
 * QR novo enquanto o celular não lê; o canal aparece na configuração quando a
 * leitura é confirmada.
 */
export async function conectarWhatsapp(): Promise<ConexaoWhatsapp> {
  const { data } = await sec.post<ConexaoWhatsapp>(`${BASE}/canal/whatsapp/conectar`, { aceite_risco: true });
  return data;
}

/** O que a tela precisa para abrir o cadastro embutido da Meta. */
export interface CadastroMeta {
  disponivel: boolean;
  app_id?: string | null;
  config_id?: string | null;
  versao?: string | null;
}

export async function fetchCadastroMeta(): Promise<CadastroMeta> {
  const { data } = await api.get<CadastroMeta>(`${BASE}/canal/meta/parametros`);
  return data;
}

/** Fecha o cadastro embutido: o servidor troca o código pelo token e grava o canal. */
export async function conectarMeta(input: {
  codigo: string;
  waba_id: string;
  phone_number_id?: string | null;
  app_business: boolean;
}): Promise<Canal> {
  const { data } = await sec.post<Canal>(`${BASE}/canal/meta/conectar`, input);
  return data;
}

export async function desconectarMeta(): Promise<Canal | null> {
  const { data } = await sec.post<Canal | null>(`${BASE}/canal/meta/desconectar`);
  return data;
}

/** Número que o Secretário nunca atende (colega, cartório, família). */
export interface NumeroIgnorado {
  id: string;
  telefone: string;
  descricao?: string | null;
  created_at: string;
}

export async function fetchNumerosIgnorados(): Promise<NumeroIgnorado[]> {
  const { data } = await sec.get<NumeroIgnorado[]>(`${BASE}/numeros-ignorados`);
  return data;
}

export async function incluirNumeroIgnorado(input: { telefone: string; descricao?: string }): Promise<NumeroIgnorado> {
  const { data } = await sec.post<NumeroIgnorado>(`${BASE}/numeros-ignorados`, input);
  return data;
}

export async function removerNumeroIgnorado(id: string): Promise<void> {
  await sec.delete(`${BASE}/numeros-ignorados/${id}`);
}

/** Encerra a sessão do número: o Secretário para de atender por ele. */
export async function desconectarWhatsapp(): Promise<Canal | null> {
  const { data } = await sec.post<Canal | null>(`${BASE}/canal/whatsapp/desconectar`);
  return data;
}

// -- Áreas --------------------------------------------------------------------
export async function criarArea(payload: AreaInput): Promise<Area> {
  const { data } = await sec.post<Area>(`${BASE}/areas`, payload);
  return data;
}

export async function atualizarArea(areaId: string, payload: Partial<AreaInput>): Promise<Area> {
  const { data } = await sec.put<Area>(`${BASE}/areas/${areaId}`, payload);
  return data;
}

export async function excluirArea(areaId: string): Promise<void> {
  await sec.delete(`${BASE}/areas/${areaId}`);
}

// -- Documentos requeridos (por área) -----------------------------------------
export async function criarDocumento(areaId: string, payload: DocumentoRequeridoInput): Promise<DocumentoRequerido> {
  const { data } = await sec.post<DocumentoRequerido>(`${BASE}/areas/${areaId}/documentos`, payload);
  return data;
}

export async function excluirDocumento(areaId: string, documentoId: string): Promise<void> {
  await sec.delete(`${BASE}/areas/${areaId}/documentos/${documentoId}`);
}

// -- Perguntas de qualificação (por área) -------------------------------------
export async function criarPergunta(areaId: string, payload: PerguntaInput): Promise<PerguntaQualificacao> {
  const { data } = await sec.post<PerguntaQualificacao>(`${BASE}/areas/${areaId}/perguntas`, payload);
  return data;
}

export async function excluirPergunta(areaId: string, perguntaId: string): Promise<void> {
  await sec.delete(`${BASE}/areas/${areaId}/perguntas/${perguntaId}`);
}

// -- Base universal (documentos/perguntas no nível da config) ------------------
export async function criarDocumentoBase(payload: DocumentoRequeridoInput): Promise<DocumentoRequerido> {
  const { data } = await sec.post<DocumentoRequerido>(`${BASE}/documentos-base`, payload);
  return data;
}

export async function excluirDocumentoBase(documentoId: string): Promise<void> {
  await sec.delete(`${BASE}/documentos-base/${documentoId}`);
}

export async function criarPerguntaBase(payload: PerguntaInput): Promise<PerguntaQualificacao> {
  const { data } = await sec.post<PerguntaQualificacao>(`${BASE}/perguntas-base`, payload);
  return data;
}

export async function excluirPerguntaBase(perguntaId: string): Promise<void> {
  await sec.delete(`${BASE}/perguntas-base/${perguntaId}`);
}

// -- Catálogo de áreas comuns (adotar área pré-preenchida) --------------------
export interface AreaCatalogo {
  chave: string;
  nome: string;
  documentos: number;
  perguntas: number;
}

export async function fetchCatalogoAreas(): Promise<AreaCatalogo[]> {
  const { data } = await sec.get<AreaCatalogo[]>(`${BASE}/catalogo/areas`);
  return data;
}

/** ``advogadoId``: de quem é a área, na do escritório (sem ele, o padrão do backend). */
export async function adotarArea(chave: string, advogadoId?: string | null): Promise<Area> {
  const { data } = await sec.post<Area>(`${BASE}/areas/adotar`, { chave, advogado_id: advogadoId ?? null });
  return data;
}

/** As áreas do catálogo marcadas ficam ativas (adotando as que faltam); as desmarcadas, inativas. */
export async function selecionarAreasCatalogo(chaves: string[], advogadoId?: string | null): Promise<Configuracao> {
  const { data } = await sec.put<Configuracao>(`${BASE}/catalogo/areas`, { chaves, advogado_id: advogadoId ?? null });
  return data;
}

// -- Modelos para os seletores de template ------------------------------------
/**
 * Modelos vinculáveis a uma área na finalidade dada (sistema + do advogado). O
 * backend aplica a MESMA regra que o link/prontidão aceitam, então o seletor não
 * mostra um modelo que o backend recusa no save (422).
 */
export async function fetchModelosPorFinalidade(
  finalidade: string,
  advogadoId?: string | null,
): Promise<ModeloResumo[]> {
  const params = advogadoId ? { finalidade, advogado_id: advogadoId } : { finalidade };
  const { data } = await sec.get<ModeloResumo[]>(`${BASE}/modelos`, { params });
  return data;
}

// -- Conversas do secretário (leitura — §9.1) ---------------------------------
// ---------------------------------------------------------------------------
// Atendimentos (docs/PLANO_SECRETARIO_ATENDIMENTOS.md) — espelha
// backend/app/schemas/secretario_conversas.py. Rótulos honestos: "devolvido
// pelo cliente", nunca "assinado"; "conferido" só para o que o cliente confirmou.
// ---------------------------------------------------------------------------

export type Fila = 'aguardando' | 'preparacao' | 'andamento' | 'todos';

export interface ConversaResumo {
  id: string;
  estado: string;
  /** Rótulo humano do estado ("Coleta de documentos", "Concluída"). */
  etapa?: string | null;
  intencao?: string | null;
  motivo_escalonamento?: string | null;
  escalonamento_rotulo?: string | null;
  assumido_por_nome?: string | null;
  contato_nome?: string | null;
  contato_telefone: string;
  cliente_id?: string | null;
  area_nome?: string | null;
  /** Quem cuida: o advogado escolhido (no escritório, nulo = ainda sem advogado). */
  advogado_id?: string | null;
  advogado_nome?: string | null;
  concluido_em?: string | null;
  prazo_ate?: string | null;
  retorno_em?: string | null;
  assumido_em?: string | null;
  desfecho?: 'PROCESSO' | 'ENCERRADA' | string | null;
  desfecho_em?: string | null;
  precisa_de_mim: boolean;
  em_preparacao: boolean;
  ultima_atividade_em: string;
  created_at: string;
}

export interface Contagens {
  aguardando: number;
  preparacao: number;
  andamento: number;
  todos: number;
  /** Do escritório, para quem vê todos: os que pararam antes da escolha do advogado. */
  sem_advogado?: number;
}

export interface MensagemConversa {
  id: string;
  direcao: 'ENTRADA' | 'SAIDA';
  conteudo?: string | null;
  created_at: string;
}

export interface ArquivoAssinado {
  nome: string;
  /** sha256 dos bytes recebidos — a prova de que o arquivo no acervo é o que chegou. */
  hash: string;
  tamanho?: number | null;
  mime?: string | null;
  /** Baixável por arquivo: `artefato_assinado_id` aponta só para o primeiro. */
  artefato_id?: string | null;
}

export interface InstrumentoConversa {
  id: string;
  tipo: 'CONTRATO_HONORARIOS' | 'PROCURACAO' | string;
  titulo?: string | null;
  status: string;
  /** PDF que foi enviado ao cliente. */
  artefato_id?: string | null;
  /** O que voltou. */
  artefato_assinado_id?: string | null;
  enviado_em?: string | null;
  recebido_em?: string | null;
  devolvido_por?: string | null;
  /** "PDF" ou "FOTOS", derivado dos arquivos devolvidos. */
  como_voltou?: string | null;
  /** Veredito da conferência por conteúdo (CONFIRMADO/INDETERMINADO/RECUSADO); nulo = não conferido. */
  conferencia?: string | null;
  arquivos: ArquivoAssinado[];
}

export interface PerguntaRespondida {
  texto: string;
  /** O que o cliente respondeu; ausente quando a pergunta não foi feita. */
  resposta?: string | null;
  /** Se ela travava o atendimento — o que não trava só é perguntado se a conversa abrir espaço. */
  obrigatoria: boolean;
  condicao?: string | null;
  /** Resposta de uma pergunta que o advogado apagou do questionário depois. */
  removida?: boolean;
}

export interface CampoLido {
  campo: string;
  rotulo: string;
  lido?: string | null;
  corrigido?: string | null;
}

export interface ArquivoDocumento {
  artefato_id: string;
  nome: string;
  mime: string;
  tamanho?: number | null;
}

export interface DocumentoDoAtendimento {
  item_nome: string;
  item_tipo?: string | null;
  item_condicao?: string | null;
  item_obrigatorio: boolean;
  /** Item da base (identidade, comprovante): qualifica o cliente e trava a coleta. */
  qualificacao: boolean;
  status: string;
  rotulo_status: string;
  conferido: boolean;
  recebido_em?: string | null;
  campos: CampoLido[];
  motivo?: string | null;
  arquivos: ArquivoDocumento[];
  substituiu_anterior: boolean;
}

export interface ClienteDoAtendimento {
  id: string;
  nome: string;
  criado_neste_atendimento: boolean;
}

export interface ProcessoVinculado {
  id: string;
  numero: string;
  classe?: string | null;
  tribunal?: string | null;
  status?: string | null;
  /** False quando o processo saiu do escopo do advogado; o vínculo continua. */
  no_escopo?: boolean;
}

export interface Seguimento {
  concluido_em?: string | null;
  prazo_retorno_horas: number;
  prazo_ate?: string | null;
  prazo_vencido: boolean;
  retorno_em?: string | null;
  retorno_por_nome?: string | null;
  retorno_observacao?: string | null;
  atraso_avisado_em?: string | null;
  desfecho?: string | null;
  desfecho_em?: string | null;
  desfecho_por_nome?: string | null;
  desfecho_motivo?: string | null;
  processo?: ProcessoVinculado | null;
}

export interface Escalonamento {
  categoria: string;
  rotulo: string;
  /** "ia": o motivo e o resumo foram escritos pelo modelo; "sistema": frase nossa. */
  origem: 'ia' | 'sistema' | string;
  resumo?: string | null;
  motivo?: string | null;
  escalado_em?: string | null;
  assumido_em?: string | null;
  assumido_por_nome?: string | null;
  retorno_em?: string | null;
  atendimento_origem_id?: string | null;
}

export interface EventoDoAtendimento {
  em: string;
  evento: string;
}

export interface ConversaDetalhe {
  conversa: ConversaResumo;
  cliente?: ClienteDoAtendimento | null;
  representacao?: Record<string, unknown> | null;
  seguimento?: Seguimento | null;
  escalonamento?: Escalonamento | null;
  documentos: DocumentoDoAtendimento[];
  instrumentos: InstrumentoConversa[];
  questionario: PerguntaRespondida[];
  mensagens: MensagemConversa[];
  linha_do_tempo: EventoDoAtendimento[];
  /** O advogado falou com o contato hoje pelo próprio WhatsApp: o Secretário está quieto até o fim do dia. */
  secretario_pausado?: boolean;
  /** O número está na lista dos que o Secretário nunca atende. */
  numero_ignorado?: boolean;
  /** De qual Secretário é o atendimento. */
  configuracao_id?: string | null;
  /** Quem vê pode passá-lo a outro advogado (o gestor, na do escritório). */
  pode_trocar_advogado?: boolean;
  /** Quem vê gere o Secretário (o dono ou o gestor): o número ignorado vale para todos. */
  pode_gerir?: boolean;
}

/** O advogado devolve o contato ao Secretário antes do fim do dia. */
export async function devolverAoSecretario(conversaId: string): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/devolver`);
  return data;
}

/** O Secretário passa a nunca atender o número deste contato. */
export async function ignorarNumeroDoContato(conversaId: string): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/ignorar-numero`);
  return data;
}

export async function fetchConversas(params?: {
  fila?: Fila;
  estado?: string;
  cliente_id?: string;
  /** Quem cuida: só os deste advogado (o gestor, na do escritório). */
  advogado_id?: string;
  /** Só os que pararam antes da escolha do advogado. */
  sem_advogado?: boolean;
  offset?: number;
  limit?: number;
}): Promise<ConversaResumo[]> {
  const { data } = await sec.get<ConversaResumo[]>('/secretario/conversas', { params });
  return data;
}

export async function fetchContagens(advogadoId?: string | null): Promise<Contagens> {
  const { data } = await sec.get<Contagens>('/secretario/conversas/contagens', {
    params: advogadoId ? { advogado_id: advogadoId } : undefined,
  });
  return data;
}

export async function fetchConversaDetalhe(conversaId: string): Promise<ConversaDetalhe> {
  const { data } = await api.get<ConversaDetalhe>(`/secretario/conversas/${conversaId}`);
  return data;
}

export async function registrarRetorno(conversaId: string, observacao?: string | null): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/retorno`, {
    observacao: observacao ?? null,
  });
  return data;
}

export async function assumirAtendimento(conversaId: string): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/assumir`);
  return data;
}

export interface DesfechoInput {
  tipo: 'PROCESSO' | 'ENCERRADA';
  motivo?: string | null;
  processo_id?: string | null;
}

export async function registrarDesfecho(conversaId: string, input: DesfechoInput): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/desfecho`, input);
  return data;
}

// -- Secretário do escritório (docs/PLANO_SECRETARIO_ESCRITORIO.md) ------------
/** Os Secretários que o usuário enxerga: o pessoal, os que gere, os em que participa. */
export async function fetchAcessiveis(): Promise<ConfiguracaoAcessivel[]> {
  const { data } = await api.get<ConfiguracaoAcessivel[]>(`${BASE}/acessiveis`);
  return data;
}

export async function incluirParticipante(usuarioId: string): Promise<Configuracao> {
  const { data } = await sec.post<Configuracao>(`${BASE}/participantes`, { usuario_id: usuarioId });
  return data;
}

export async function removerParticipante(usuarioId: string): Promise<Configuracao> {
  const { data } = await sec.delete(`${BASE}/participantes/${usuarioId}`);
  return data as Configuracao;
}

export async function reordenarParticipantes(usuarioIds: string[]): Promise<Configuracao> {
  const { data } = await sec.put<Configuracao>(`${BASE}/participantes/ordem`, { usuario_ids: usuarioIds });
  return data;
}

export interface PerfilInput {
  tratamento?: TratamentoAdvogado | null;
  nome_tratamento?: string | null;
  honorarios?: Record<string, unknown> | null;
  template_contrato_id?: string | null;
  template_procuracao_id?: string | null;
  advogado_endereco?: EnderecoAdvogado | null;
}

export async function atualizarPerfil(usuarioId: string, payload: PerfilInput): Promise<Configuracao> {
  const { data } = await sec.put<Configuracao>(`${BASE}/participantes/${usuarioId}`, payload);
  return data;
}

/** "Copiar áreas de…": as que o advogado ainda não tem, com honorários, documentos e perguntas. */
export async function copiarAreas(paraUsuarioId: string, deUsuarioId: string): Promise<Configuracao> {
  const { data } = await sec.post<Configuracao>(`${BASE}/participantes/${paraUsuarioId}/copiar-areas`, {
    de_usuario_id: deUsuarioId,
  });
  return data;
}

/**
 * Passa o atendimento encerrado a outro advogado (o gestor). Com contrato ou
 * procuração já feitos, responde 409 com ``codigo: 'instrumentos_gerados'`` até vir
 * ``confirmar``.
 */
export async function trocarAdvogado(
  conversaId: string,
  advogadoId: string,
  confirmar = false,
): Promise<ConversaDetalhe> {
  const { data } = await api.post<ConversaDetalhe>(`/secretario/conversas/${conversaId}/advogado`, {
    advogado_id: advogadoId,
    confirmar,
  });
  return data;
}
